import { Link } from "wouter";
import { UtensilsCrossed, Globe, Settings, Wallet, BarChart3, Users, Ticket, ChevronRight, Zap, Megaphone, MessageSquare } from "lucide-react";
import PartnerLayout from "./PartnerLayout";

interface ToolItem {
  icon: typeof UtensilsCrossed;
  title: string;
  description: string;
  href: string;
}

const restaurantTools: ToolItem[] = [
  {
    icon: UtensilsCrossed,
    title: 'Menu',
    description: 'Edit your menu items and prices',
    href: '/partner/menu',
  },
  {
    icon: Globe,
    title: 'Website',
    description: 'Edit your public restaurant page',
    href: '/partner/website',
  },
  {
    icon: MessageSquare,
    title: 'Reviews',
    description: 'Respond to Eatzy customer reviews',
    href: '/partner/reviews',
  },
  {
    icon: Settings,
    title: 'Settings',
    description: 'Account, social connections, and integrations',
    href: '/partner/settings',
  },
];

const growthTools: ToolItem[] = [
  {
    icon: Zap,
    title: 'Loyalty Programs',
    description: 'Kudoz reward programs for repeat customers',
    href: '/partner/loyalty',
  },
  {
    icon: Megaphone,
    title: 'Campaigns',
    description: 'Promotions and targeted customer campaigns',
    href: '/partner/campaigns',
  },
  {
    icon: Wallet,
    title: 'Kudoz Wallet',
    description: 'View balance, send gift certificates',
    href: '/partner/kudoz',
  },
  {
    icon: BarChart3,
    title: 'Analytics',
    description: 'Detailed sales, loyalty trends, and insights',
    href: '/partner/analytics',
  },
  {
    icon: Users,
    title: 'Influencers',
    description: 'Connect with local food creators',
    href: '/partner/influencers',
  },
  {
    icon: Ticket,
    title: 'Coupons',
    description: 'Manage discount codes',
    href: '/partner/coupons',
  },
];

function ToolRow({ item }: { item: ToolItem }) {
  const Icon = item.icon;
  return (
    <Link href={item.href}>
      <div
        className="flex items-center gap-4 px-4 py-3.5 hover-elevate cursor-pointer border-b last:border-b-0"
        data-testid={`tool-row-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="p-2 bg-muted rounded-md shrink-0">
          <Icon className="h-4 w-4 text-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{item.title}</p>
          <p className="text-xs text-muted-foreground">{item.description}</p>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>
    </Link>
  );
}

export default function PartnerTools() {
  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-tools-title">More</h1>
          <p className="text-muted-foreground">Manage your restaurant and growth tools</p>
        </div>

        <div className="space-y-6">
          <section>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 px-1">Restaurant</p>
            <div className="rounded-lg border overflow-hidden bg-card">
              {restaurantTools.map((item) => (
                <ToolRow key={item.href} item={item} />
              ))}
            </div>
          </section>

          <section>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 px-1">Growth &amp; Loyalty</p>
            <div className="rounded-lg border overflow-hidden bg-card">
              {growthTools.map((item) => (
                <ToolRow key={item.href} item={item} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </PartnerLayout>
  );
}
